import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import BsModal from 'react-bootstrap/Modal';
import { FaExclamationTriangle, FaLock, FaTrash } from 'react-icons/fa';
import DebtService from '../../services/debt.service';
import { useLabels } from '../../hooks/useLabels';
import { toast } from '../../toasts';
import { getError, showCurrency } from '../../utils';
import { Button, controlClass } from '../EntityList';

// Main entities are itemised one row per record; system-generated ones are
// summarised as counts. Nothing is individually acknowledged — the single gate
// is typing the server-returned debt label, so the summary is purely informational.
const SECTIONS = [
	{ key: 'payments', labelKey: 'entity.payment.plural' },
	{ key: 'interests', labelKey: 'entity.interest.plural' },
	{ key: 'warranties', labelKey: 'entity.warranty.plural' },
	{ key: 'documents', labelKey: 'entity.document.plural' },
];

const formatAmount = (amount, currency) => {
	if (amount === undefined || amount === null) return '';
	const value = new Intl.NumberFormat('es-UY').format(amount);
	return currency ? `${showCurrency(currency) || currency} ${value}` : value;
};

const formatDate = (value) => (value ? String(value).slice(0, 10) : '');

/** The one-line detail under each row's title. */
const describe = (sectionKey, item) => {
	const parts = [];
	switch (sectionKey) {
	case 'payments':
		parts.push(formatAmount(item.amount, item.currency), formatDate(item.date), item.paymentMethod);
		break;
	case 'interests':
		if (item.percentage !== undefined && item.percentage !== null) parts.push(`${item.percentage}%`);
		break;
	case 'warranties':
		parts.push(item.option, formatAmount(item.amount, item.currency));
		break;
	case 'documents':
		parts.push(item.documentType);
		break;
	default:
		break;
	}
	return parts.filter(Boolean).join(' · ');
};

/** Preview and exact server-label confirmation for a transactional debt cascade. */
function DebtDeletionModal({ show, debt, onClose, onDeleted }) {
	const { t } = useLabels();
	const [state, setState] = useState('loading');
	const [preview, setPreview] = useState(null);
	const [error, setError] = useState('');
	const [confirmLabel, setConfirmLabel] = useState('');

	const debtId = debt?._id;
	const debtLabel = preview?.debt?.label || '';
	const previewRequest = useRef(0);
	const submitting = useRef(false);

	const loadPreview = useCallback(async () => {
		const request = ++previewRequest.current;
		setState('loading');
		setPreview(null);
		setConfirmLabel('');
		setError('');
		try {
			const { data } = await DebtService.getDeletionPreview(debtId);
			if (request !== previewRequest.current) return;
			setPreview(data);
			setState(data.blocked ? 'blocked' : 'idle');
		} catch (err) {
			if (request !== previewRequest.current) return;
			setError(getError(err));
			setState('error');
		}
	}, [debtId]);

	useEffect(() => {
		if (show && debtId) loadPreview();
		return () => { previewRequest.current++; };
	}, [show, debtId, loadPreview]);

	// Reset on exit rather than on close, so the body doesn't flip back to a
	// spinner while the modal is still fading out.
	const handleExited = () => {
		setPreview(null);
		setConfirmLabel('');
		setState('loading');
		setError('');
	};

	const sections = useMemo(() => {
		if (!preview) return [];
		return SECTIONS.map((section) => ({
			...section,
			label: t(section.labelKey),
			items: preview.mainEntities?.[section.key] || [],
		})).filter((section) => section.items.length > 0);
	}, [preview, t]);

	const generated = preview?.generatedEntities || [];

	const labelMatches = debtLabel !== '' && confirmLabel === debtLabel;
	const canDelete = show && state === 'idle' && preview?.debt?._id === debtId && labelMatches;

	const handleDelete = async () => {
		if (!canDelete || submitting.current) return;
		submitting.current = true;
		setState('deleting');
		try {
			await DebtService.delete(debtId);
		} catch (err) {
			// A 409 carries a fresh preview: the graph changed since we loaded it,
			// so re-render from the response instead of just toasting.
			const conflicts = err?.response?.data?.conflicts;
			if (err?.response?.status === 409 && conflicts) {
				setPreview(err.response.data);
				setConfirmLabel('');
				setState(err.response.data.blocked ? 'blocked' : 'idle');
			} else {
				setState('idle');
			}
			toast.error(getError(err));
			return;
		} finally {
			submitting.current = false;
		}
		toast.success(t('msg.debtDeleted'));
		onDeleted();
	};

	const busy = state === 'deleting';

	return (
		<BsModal
			show={show}
			onHide={busy ? undefined : onClose}
			onExited={handleExited}
			centered
			scrollable
			backdrop={busy ? 'static' : true}
			keyboard={!busy}
			dialogClassName='tw:max-w-[600px]'
			contentClassName='entity-list-page el-modal tw:rounded-[14px] tw:border-0 tw:overflow-hidden tw:shadow-overlay tw:font-sans tw:text-[14px]/[1.45] tw:text-ink-0'
		>
			<BsModal.Header
				closeButton={!busy}
				className='tw:items-start tw:border-b tw:border-border tw:px-[22px] tw:pt-[20px] tw:pb-[14px]'
			>
				<div className='tw:flex tw:items-start tw:gap-3'>
					<span className='tw:mt-0.5 tw:flex tw:h-[38px] tw:w-[38px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[9px] tw:bg-danger-soft tw:text-danger'>
						{state === 'blocked' ? <FaLock /> : <FaTrash />}
					</span>
					<div>
						<BsModal.Title className='tw:text-[17px]/[1.3] tw:font-bold tw:tracking-[-0.01em] tw:text-ink-0'>
							{t('title.deleteDebt')}
						</BsModal.Title>
						<p className='tw:mb-0 tw:mt-1 tw:text-[12.5px] tw:text-ink-2'>{debtLabel}</p>
					</div>
				</div>
			</BsModal.Header>

			{state === 'loading' && (
				<BsModal.Body className='tw:flex tw:flex-col tw:items-center tw:gap-1 tw:px-[22px] tw:py-12 tw:text-center'>
					<div className='tw:mb-3 tw:h-14 tw:w-14 tw:animate-spin tw:rounded-full tw:border-4 tw:border-surface-3 tw:border-t-accent' />
					<div className='tw:text-[12.5px] tw:text-ink-3'>Analizando registros relacionados…</div>
				</BsModal.Body>
			)}

			{state === 'deleting' && (
				<BsModal.Body className='tw:flex tw:flex-col tw:items-center tw:gap-1 tw:px-[22px] tw:py-12 tw:text-center'>
					<div className='tw:mb-3 tw:h-14 tw:w-14 tw:animate-spin tw:rounded-full tw:border-4 tw:border-surface-3 tw:border-t-danger' />
					<div className='tw:font-semibold tw:text-ink-0'>{t('msg.deleteDebtDeleting')}</div>
					<div className='tw:text-[12.5px] tw:text-ink-3'>No cierre esta ventana</div>
				</BsModal.Body>
			)}

			{state === 'error' && (
				<>
					<BsModal.Body className='tw:px-[22px] tw:py-[18px]'>
						<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-3 tw:py-2.5 tw:text-[12.5px] tw:text-danger'>
							{error || t('msg.deleteDebtPreviewFailed')}
						</div>
					</BsModal.Body>
					<BsModal.Footer className='tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-[14px] tw:gap-2'>
						<Button variant='default' onClick={onClose}>
							{t('action.cancel')}
						</Button>
						<Button variant='primary' onClick={loadPreview}>
							Reintentar
						</Button>
					</BsModal.Footer>
				</>
			)}

			{state === 'blocked' && (
				<>
					<BsModal.Body className='tw:flex tw:flex-col tw:gap-3.5 tw:px-[22px] tw:py-[18px]'>
						<div className='tw:rounded-[7px] tw:border tw:border-warn tw:bg-warn-soft tw:px-3 tw:py-2.5 tw:text-[12.5px] tw:text-ink-1'>
							{preview?.totals?.blockedBySize
								? `La deuda tiene ${preview.totals.documentCount} registros relacionados, más de los ${preview.totals.maxDocumentCount} que se pueden eliminar en una sola operación. Reduzca los registros relacionados antes de volver a intentar.`
								: t('msg.deleteDebtBlocked')}
						</div>
						{(preview?.conflicts || []).length > 0 && (
							// Capped so the explanation above and the footer stay in view no
							// matter how many records are blocking.
							<div className='tw:max-h-[320px] tw:overflow-y-auto tw:rounded-token tw:border tw:border-border tw:bg-surface'>
								{preview.conflicts.map((conflict) => (
									<div
										key={`${conflict.code}:${conflict._id}`}
										className='tw:flex tw:items-start tw:gap-2.5 tw:border-0 tw:border-b tw:border-border tw:px-3 tw:py-2.5 tw:last:border-b-0'
									>
										<span className='tw:mt-0.5 tw:shrink-0 tw:text-warn'>
											<FaExclamationTriangle />
										</span>
										<div>
											<div className='tw:text-[13.5px] tw:font-medium tw:text-ink-0'>{conflict.label}</div>
											<div className='tw:text-[11.5px] tw:text-ink-3'>{conflict.message}</div>
										</div>
									</div>
								))}
							</div>
						)}
					</BsModal.Body>
					<BsModal.Footer className='tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-[14px] tw:gap-2'>
						<Button variant='default' onClick={onClose}>
							Cerrar
						</Button>
					</BsModal.Footer>
				</>
			)}

			{state === 'idle' && (
				<>
					<BsModal.Body className='tw:flex tw:flex-col tw:gap-3.5 tw:px-[22px] tw:py-[18px]'>
						<p className='tw:mb-0 tw:text-[13px] tw:text-ink-2'>
							{sections.length === 0 && generated.length === 0
								? t('msg.confirmDeleteDebt')
								: t('msg.deleteDebtIntro')}
						</p>

						{(preview?.warnings || []).map((warning) => (
							<div
								key={warning.code}
								className='tw:rounded-[7px] tw:border tw:border-warn tw:bg-warn-soft tw:px-3 tw:py-2.5 tw:text-[12px] tw:text-ink-1'
							>
								{warning.code === 'COLLECTED_PAYMENTS'
									? t('msg.deleteDebtCollectedPayments')
										.replace('{count}', warning.paymentCount)
										.replace('{total}', (warning.totalCollected || []).map(({ currency, amount }) => formatAmount(amount, currency)).join(' · ') || '0')
										.replace('{paidDueCount}', warning.paidDueCount)
									: warning.message}
							</div>
						))}

						{sections.map((section) => (
							<div key={section.key}>
								<div className='tw:mb-2 tw:flex tw:items-center tw:justify-between'>
									<span className='tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>
										{section.label}
									</span>
									<span className='tw:text-[11px] tw:text-ink-3'>{section.items.length}</span>
								</div>
								<div className='tw:max-h-[320px] tw:overflow-y-auto tw:rounded-token tw:border tw:border-border tw:bg-surface'>
									{section.items.map((item) => {
										const detail = describe(section.key, item);
										return (
											<div
												key={item._id}
												className='tw:border-0 tw:border-b tw:border-border tw:px-3 tw:py-2 tw:last:border-b-0'
											>
												<div className='tw:truncate tw:text-[13.5px] tw:text-ink-0'>{item.label}</div>
												{detail && <div className='tw:text-[11.5px] tw:text-ink-3'>{detail}</div>}
											</div>
										);
									})}
								</div>
							</div>
						))}

						{generated.length > 0 && (
							<div>
								<div className='tw:mb-2 tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>
									{t('msg.deleteDebtGenerated')}
								</div>
								<div className='tw:max-h-[320px] tw:overflow-y-auto tw:rounded-token tw:border tw:border-border tw:bg-surface'>
									{generated.map((group) => (
										<div
											key={group.key}
											className='tw:flex tw:items-center tw:gap-2 tw:border-0 tw:border-b tw:border-border tw:px-3 tw:py-2 tw:last:border-b-0'
										>
											<span className='tw:flex-1 tw:text-[13.5px] tw:text-ink-0'>{group.label}</span>
											<span className='tw:text-[13px] tw:font-semibold tw:text-ink-1'>{group.count}</span>
										</div>
									))}
								</div>
							</div>
						)}

						{(preview?.preserved || []).length > 0 && (
							<div>
								<div className='tw:mb-2 tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>
									{t('msg.deleteDebtPreserved')}
								</div>
								<div className='tw:overflow-hidden tw:rounded-token tw:border tw:border-border tw:bg-surface-2'>
									{preview.preserved.map((group) => (
										<div
											key={group.key}
											className='tw:flex tw:items-center tw:gap-2 tw:border-0 tw:border-b tw:border-border tw:px-3 tw:py-2 tw:last:border-b-0'
										>
											<span className='tw:shrink-0 tw:text-ink-3'>
												<FaLock size={11} />
											</span>
											<div className='tw:flex-1'>
												<span className='tw:text-[13px] tw:text-ink-1'>{group.label}</span>
												<span className='tw:ml-2 tw:text-[11.5px] tw:text-ink-3'>{group.reason}</span>
											</div>
											<span className='tw:text-[13px] tw:text-ink-2'>{group.count}</span>
										</div>
									))}
								</div>
							</div>
						)}

						<div className='tw:rounded-[7px] tw:border tw:border-border tw:bg-surface-2 tw:px-3 tw:py-3'>
							<label htmlFor='delete-debt-confirm' className='tw:mb-1.5 tw:block tw:text-[12.5px] tw:text-ink-1'>
								{t('msg.deleteDebtTypeId')}{' '}
								<span className='tw:font-semibold tw:text-ink-0'>{debtLabel}</span>
							</label>
							<input
								id='delete-debt-confirm'
								type='text'
								autoComplete='off'
								autoFocus
								value={confirmLabel}
								onChange={(event) => setConfirmLabel(event.target.value)}
								onKeyDown={(event) => {
									if (event.key === 'Enter') handleDelete();
								}}
								className={`${controlClass} tw:w-full tw:px-2.5`}
							/>
							{confirmLabel.length > 0 && !labelMatches && (
								<div className='tw:mt-1.5 tw:text-[11.5px] tw:text-danger'>{t('msg.deleteDebtIdMismatch')}</div>
							)}
						</div>
					</BsModal.Body>
					<BsModal.Footer className='tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-[14px] tw:gap-2'>
						<Button variant='default' onClick={onClose}>
							{t('action.cancel')}
						</Button>
						<Button
							variant='primary'
							icon={<FaTrash />}
							disabled={!canDelete}
							className={!canDelete ? 'tw:opacity-50' : ''}
							onClick={handleDelete}
						>
							{t('action.delete')}
						</Button>
					</BsModal.Footer>
				</>
			)}
		</BsModal>
	);
}

DebtDeletionModal.propTypes = {
	show: PropTypes.bool.isRequired,
	debt: PropTypes.shape({
		_id: PropTypes.string,
		customId: PropTypes.string,
		concept: PropTypes.string,
	}),
	onClose: PropTypes.func.isRequired,
	onDeleted: PropTypes.func.isRequired,
};

export default DebtDeletionModal;
